import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/datetime";
import CampaignWizard from "@/components/CampaignWizard";
import { PURPOSES, DIAL_MODES, STATUS_STYLES, labelFor } from "@/lib/campaignMeta";
import { Megaphone, Plus, Copy, Archive, Pause, Play, ArrowRight, ArrowCounterClockwise } from "@phosphor-icons/react";

export default function Campaigns() {
    const { orgTimezone } = useAuth();
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [wizard, setWizard] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/campaigns");
            setCampaigns(data);
        } catch {
            /* ignore */
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const act = async (id, fn, label) => {
        try {
            await fn();
            toast.success(label);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
        }
    };
    const setStatus = (id, status, label) => act(id, () => api.post(`/campaigns/${id}/status`, { status }), label);
    const duplicate = (id) => act(id, () => api.post(`/campaigns/${id}/duplicate`), "Campaign duplicated");

    return (
        <AppShell>
            <div className="mx-auto max-w-6xl px-6 py-8 md:px-12 md:py-12" data-testid="campaigns-page">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2"><Megaphone size={14} weight="fill" /> Outbound</div>
                        <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight sm:text-5xl">Campaigns.</h1>
                        <p className="mt-2 text-sm text-stone2">Run outbound sales, follow-ups, collections, renewals and reminders.</p>
                    </div>
                    <button onClick={() => setWizard(true)} data-testid="create-campaign-btn" className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5">
                        <Plus size={16} weight="bold" /> Create Campaign
                    </button>
                </div>

                <div className="mt-8 overflow-hidden rounded-2xl border border-stone-200 bg-white">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px] text-left text-sm">
                            <thead className="border-b border-stone-200 bg-stone-50 text-[11px] font-bold uppercase tracking-wider text-stone2">
                                <tr>
                                    <th className="px-4 py-3">Campaign</th>
                                    <th className="px-4 py-3">Type</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Team</th>
                                    <th className="px-4 py-3">Records</th>
                                    <th className="px-4 py-3">Mode</th>
                                    <th className="px-4 py-3 w-40">Progress</th>
                                    <th className="px-4 py-3">Success</th>
                                    <th className="px-4 py-3">Created</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && <tr><td colSpan={10} className="px-4 py-10 text-center text-stone2">Loading…</td></tr>}
                                {!loading && campaigns.length === 0 && (
                                    <tr><td colSpan={10} className="px-4 py-16 text-center">
                                        <Megaphone size={30} className="mx-auto text-stone-300" />
                                        <div className="mt-3 font-semibold text-ink">No campaigns yet</div>
                                        <div className="mt-1 text-sm text-stone2">Create your first outbound campaign to get started.</div>
                                    </td></tr>
                                )}
                                {campaigns.map((c) => (
                                    <tr key={c.id} data-testid={`campaign-row-${c.id}`} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/60">
                                        <td className="px-4 py-3">
                                            <button onClick={() => navigate(`/campaigns/${c.id}`)} className="font-semibold text-ink hover:text-brand">{c.name}</button>
                                            {c.description && <div className="max-w-[220px] truncate text-xs text-stone2">{c.description}</div>}
                                        </td>
                                        <td className="px-4 py-3 text-stone2">{labelFor(PURPOSES, c.purpose)}</td>
                                        <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${STATUS_STYLES[c.status] || "bg-stone-100"}`}>{c.status}</span></td>
                                        <td className="px-4 py-3 text-stone2">{c.team || "—"}</td>
                                        <td className="px-4 py-3 tabular-nums">{c.total_records}</td>
                                        <td className="px-4 py-3 text-stone2">{labelFor(DIAL_MODES, c.dialing_mode)}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-stone-200"><div className="h-full rounded-full bg-brand" style={{ width: `${c.progress}%` }} /></div>
                                                <span className="text-xs tabular-nums text-stone2">{c.progress}%</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 tabular-nums">{c.success_rate}%</td>
                                        <td className="px-4 py-3 text-xs text-stone2">{formatDateTime(c.created_at, orgTimezone)}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                                {c.status === "active" && <IconBtn testid={`pause-${c.id}`} title="Pause" onClick={() => setStatus(c.id, "paused", "Campaign paused")}><Pause size={15} weight="fill" /></IconBtn>}
                                                {(c.status === "paused" || c.status === "draft") && <IconBtn testid={`resume-${c.id}`} title={c.status === "draft" ? "Activate" : "Resume"} onClick={() => setStatus(c.id, "active", "Campaign activated")}><Play size={15} weight="fill" /></IconBtn>}
                                                <IconBtn testid={`duplicate-${c.id}`} title="Duplicate" onClick={() => duplicate(c.id)}><Copy size={15} /></IconBtn>
                                                {c.status !== "archived" && <IconBtn testid={`archive-${c.id}`} title="Archive" onClick={() => setStatus(c.id, "archived", "Campaign archived")}><Archive size={15} /></IconBtn>}
                                                {c.status === "archived" && <IconBtn testid={`restore-${c.id}`} title="Restore to draft" onClick={() => setStatus(c.id, "draft", "Campaign restored to draft")}><ArrowCounterClockwise size={15} /></IconBtn>}
                                                <IconBtn testid={`open-${c.id}`} title="Open" onClick={() => navigate(`/campaigns/${c.id}`)}><ArrowRight size={15} /></IconBtn>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {wizard && <CampaignWizard onClose={() => setWizard(false)} onCreated={load} />}
        </AppShell>
    );
}

function IconBtn({ children, onClick, title, testid }) {
    return <button onClick={onClick} title={title} data-testid={testid} className="grid h-8 w-8 place-items-center rounded-lg text-stone2 transition-colors hover:bg-stone-200 hover:text-ink">{children}</button>;
}
